import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO } from 'date-fns';

import { jobsAPI, JobRecord, JobsListFilters, JobsListResponse } from '../services/api';
import JobDetailsModal from '../components/JobDetailsModal';

// Status color mapping
const STATUS_COLORS = {
  pending: 'warning',
  processing: 'info',
  uploading: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
} as const;

// Platform icons/colors
const PLATFORM_COLORS = {
  youtube: '#FF0000',
  meta: '#1877F2',
} as const;

// Source display names
const SOURCE_LABELS = {
  slack_dm: 'Slack DM',
  google_drive: 'Google Drive',
  web_interface: 'Web Interface',
} as const;

const JobsPage: React.FC = () => {
  // State for filters
  const [filters, setFilters] = useState<JobsListFilters>({
    limit: 100,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // State for job details modal
  const [selectedJob, setSelectedJob] = useState<JobRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch jobs with React Query
  const {
    data: jobsResponse,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery<JobsListResponse>({
    queryKey: ['jobs', filters],
    queryFn: () => jobsAPI.getAll(filters),
    refetchInterval: autoRefresh ? 10000 : false, // Refresh every 10 seconds if auto-refresh is on
    refetchIntervalInBackground: false,
    staleTime: 5000, // Data is fresh for 5 seconds
  });

  // Auto-scroll to new jobs (optional feature)
  useEffect(() => {
    // Scroll to top when jobs refresh
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [jobsResponse?.jobs]);

  const handleFilterChange = (field: keyof JobsListFilters, value: string | number | undefined) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handleClearFilters = () => {
    setFilters({ limit: 100 });
  };

  // Modal handlers
  const handleViewJobDetails = (job: JobRecord) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusChip = (status: string) => {
    const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'default';
    return (
      <Chip 
        label={status.charAt(0).toUpperCase() + status.slice(1)} 
        color={color}
        size="small"
        variant="filled"
      />
    );
  };

  const getPlatformChip = (platform: string) => {
    const color = PLATFORM_COLORS[platform as keyof typeof PLATFORM_COLORS] || '#666';
    return (
      <Chip 
        label={platform.charAt(0).toUpperCase() + platform.slice(1)}
        size="small"
        sx={{
          backgroundColor: color,
          color: 'white',
          '& .MuiChip-label': {
            fontWeight: 'bold',
          },
        }}
      />
    );
  };

  const getRelativeTime = (timestamp: string): string => {
    try {
      return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
    } catch {
      return timestamp;
    }
  };

  const getJobProgress = (job: JobRecord): number => {
    switch (job.status) {
      case 'pending': return 0;
      case 'processing': return 33;
      case 'uploading': return 66;
      case 'completed': return 100;
      case 'failed': return 100;
      case 'cancelled': return 100;
      default: return 0;
    }
  };

  const renderFilterControls = () => (
    <Card sx={{ mb: 2 }} elevation={1}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e: SelectChangeEvent) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="uploading">Uploading</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Platform</InputLabel>
              <Select
                value={filters.platform || ''}
                label="Platform"
                onChange={(e: SelectChangeEvent) => handleFilterChange('platform', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="youtube">YouTube</MenuItem>
                <MenuItem value="meta">Meta</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={filters.source || ''}
                label="Source"
                onChange={(e: SelectChangeEvent) => handleFilterChange('source', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="slack_dm">Slack DM</MenuItem>
                <MenuItem value="google_drive">Google Drive</MenuItem>
                <MenuItem value="web_interface">Web Interface</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="User ID"
              value={filters.user_id || ''}
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
              placeholder="Filter by user"
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderSummaryCards = () => {
    if (!jobsResponse?.summary) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                By Status
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(jobsResponse.summary.by_status).map(([status, count]) => (
                  <Chip
                    key={status}
                    label={`${status}: ${count}`}
                    color={STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'default'}
                    size="small"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                By Platform
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(jobsResponse.summary.by_platform).map(([platform, count]) => (
                  <Chip
                    key={platform}
                    label={`${platform}: ${count}`}
                    size="small"
                    sx={{
                      backgroundColor: PLATFORM_COLORS[platform as keyof typeof PLATFORM_COLORS] || '#666',
                      color: 'white',
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                By Source
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(jobsResponse.summary.by_source).map(([source, count]) => (
                  <Chip
                    key={source}
                    label={`${SOURCE_LABELS[source as keyof typeof SOURCE_LABELS] || source}: ${count}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderJobsTable = () => {
    if (isLoading) {
      return (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableCell key={i}>
                    <Skeleton width="100%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton width="100%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (!jobsResponse?.jobs || jobsResponse.jobs.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No jobs found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or create some upload jobs.
          </Typography>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper} elevation={2}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobsResponse.jobs.map((job) => (
              <TableRow key={job.job_id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusChip(job.status)}
                    {job.retry_count > 0 && (
                      <Chip 
                        label={`Retry ${job.retry_count}`} 
                        size="small" 
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {getPlatformChip(job.platform)}
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {job.metadata?.file_name || job.metadata?.name_en || 'Unknown'}
                    </Typography>
                    {job.metadata?.duration && (
                      <Typography variant="caption" color="text.secondary">
                        Duration: {formatDuration(job.metadata.duration)}
                      </Typography>
                    )}
                    {job.production_id && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Production: {job.production_id} | Creative: {job.creative_number}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {job.user_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {SOURCE_LABELS[job.source as keyof typeof SOURCE_LABELS] || job.source}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ width: '100%', minWidth: 80 }}>
                    <LinearProgress
                      variant="determinate"
                      value={getJobProgress(job)}
                      color={job.status === 'failed' ? 'error' : 'primary'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {getJobProgress(job)}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={new Date(job.created_at).toLocaleString()}>
                    <Typography variant="body2">
                      {getRelativeTime(job.created_at)}
                    </Typography>
                  </Tooltip>
                  {job.last_heartbeat && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last seen: {getRelativeTime(job.last_heartbeat)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => handleViewJobDetails(job)}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ padding: 3 }}>
      <>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            Jobs Dashboard
          </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              color={showFilters ? 'primary' : 'inherit'}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button
              variant={autoRefresh ? 'contained' : 'outlined'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              color={autoRefresh ? 'success' : 'inherit'}
            >
              Auto Refresh {autoRefresh ? 'On' : 'Off'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isRefetching}
            >
              {isRefetching ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
        </Box>

        {/* Error handling */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load jobs: {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        )}

        {/* Filter controls */}
        {showFilters && renderFilterControls()}

        {/* Summary cards */}
        {renderSummaryCards()}

        {/* Jobs count */}
        {jobsResponse && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {jobsResponse.jobs.length} of {jobsResponse.total_count} jobs
            {Object.values(jobsResponse.filters_applied).some(v => v && v !== 100) && ' (filtered)'}
          </Typography>
        )}

        {/* Jobs table */}
        {renderJobsTable()}
      </>

      {/* Job Details Modal */}
      <JobDetailsModal 
        open={modalOpen}
        onClose={handleCloseModal}
        job={selectedJob}
      />
    </Box>
  );
};

export default JobsPage;